import React, { useState, useEffect, useMemo } from "react";
import {
  Row, Col, Card, Typography, Table, Empty, Select, Modal,
  Button, Space, Avatar, Statistic, DatePicker, message, Spin, Tag, Divider
} from "antd";
import {
  DollarOutlined, ShoppingCartOutlined, WarningOutlined, EyeOutlined,
  BarChartOutlined, PieChartOutlined, FileTextOutlined, ArrowUpOutlined,
  ArrowDownOutlined, CalendarOutlined, RiseOutlined, FallOutlined,
  TeamOutlined, CreditCardOutlined, ShopOutlined, StarOutlined
} from "@ant-design/icons";
import { Column, Line, DualAxes } from "@ant-design/charts";
import { Pie, Doughnut } from "react-chartjs-2";
import axiosInstance from "./../../axiosInstance";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from "chart.js";
import moment from 'moment';
import { motion } from "framer-motion";
import { useTheme } from "@emotion/react";

const { Title, Text } = Typography;
const { Option } = Select;

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Enhanced color generation function
const generateComparisonColors = (data, comparisonType) => {
  // Base color palette
  const basePalette = [
    '#422AFB', '#715FFE', '#A192FD', '#D1C9FF',
    '#4FD1C5', '#38B2AC', '#319795', '#2C7A7B',
    '#F6AD55', '#ED8936', '#DD6B20', '#C05621',
    '#4299E1', '#3182CE', '#2B6CB0', '#2C5282',
    '#9F7AEA', '#805AD5', '#6B46C1', '#553C9A'
  ];

  // Generate distinct colors based on comparison type
  if (comparisonType === 'time_period') {
    // Time periods get a gradient from purple to teal
    return data.map((_, i) => {
      const ratio = i / (data.length - 1);
      return interpolateColor('#422AFB', '#4FD1C5', ratio);
    });
  } else if (comparisonType === 'products') {
    // Products get distinct colors from the base palette
    return data.map((_, i) => basePalette[i % basePalette.length]);
  } else if (comparisonType === 'customers') {
    // Customers get warm colors
    return data.map((_, i) => interpolateColor('#F6AD55', '#E53E3E', i / data.length));
  } else {
    // Default to base palette
    return data.map((_, i) => basePalette[i % basePalette.length]);
  }
};

// Helper function to interpolate between two colors
const interpolateColor = (color1, color2, factor) => {
  if (factor <= 0) return color1;
  if (factor >= 1) return color2;

  const hex = (color) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };

  const rgb1 = hex(color1);
  const rgb2 = hex(color2);

  const r = Math.round(rgb1[0] + factor * (rgb2[0] - rgb1[0]));
  const g = Math.round(rgb1[1] + factor * (rgb2[1] - rgb1[1]));
  const b = Math.round(rgb1[2] + factor * (rgb2[2] - rgb1[2]));

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const HomePage = () => {
  const [typingText] = useState("Welcome to Sell Smart Management System");
  const [salesAmount, setSalesAmount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [salesPerformance, setSalesPerformance] = useState([]);
  const [salesProgress, setSalesProgress] = useState(0);
  const [monthlyProgress, setMonthlyProgress] = useState([]);
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [productSalesData, setProductSalesData] = useState([]);
  const [todaySales, setTodaySales] = useState(0);
  const [todaySalesProducts, setTodaySalesProducts] = useState([]);
  const [salesPeriod, setSalesPeriod] = useState("today");
  const [periodSalesData, setPeriodSalesData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(moment());
  const [hourlyData, setHourlyData] = useState([]);
  const [todaysInvoices, setTodaysInvoices] = useState([]);
  const [todaysInvoicesCount, setTodaysInvoicesCount] = useState(0);
  const [periodSalesComparison, setPeriodSalesComparison] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonType, setComparisonType] = useState("time_period");
  const [comparisonMetric, setComparisonMetric] = useState("revenue");

  const shopId = localStorage.getItem("shopId");

  const allMonths = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  // Format current date and time
  const getFormattedDate = () => {
    const today = new Date();
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const day = days[today.getDay()];
    const date = today.getDate();
    const month = allMonths[today.getMonth()];
    const year = today.getFullYear();
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    const seconds = String(today.getSeconds()).padStart(2, "0");

    return `${day}, ${date} ${month} ${year} ${hours}:${minutes}:${seconds}`;
  };

  const [currentDateTime, setCurrentDateTime] = useState(getFormattedDate());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(getFormattedDate());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Fetch comparison data
  const fetchComparisonData = async () => {
    setComparisonLoading(true);
    try {
      const payload = {
        shop_id: shopId,
        comparison_type: comparisonType,
        metric: comparisonMetric
      };

      // Add type-specific parameters
      if (comparisonType === 'time_period') {
        payload.time_period = 'monthly';
        payload.granularity = 'daily';
      } else if (comparisonType === 'products') {
        payload.product_ids = topSellingProducts.slice(0, 5).map(p => p.id);
      } else if (comparisonType === 'customers') {
        // You would need to fetch some customer IDs first
      }

      const response = await axiosInstance.post('/sales/compare', payload);
      setComparisonData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      message.error("Failed to load comparison data");
    } finally {
      setComparisonLoading(false);
    }
  };

  // Fetch today's invoices
  const fetchTodaysInvoices = async () => {
    try {
      const response = await axiosInstance.get(`/sales/todays-invoices/${shopId}`);
      setTodaysInvoices(response.data.invoices || []);
      setTodaysInvoicesCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching today's invoices:", error);
    }
  };

  // Fetch hourly sales data
  const fetchHourlySales = async (selectedDate) => {
    try {
      setLoading(true);
      const formattedDate = selectedDate.format('YYYY-MM-DD');
      const response = await axiosInstance.get('/shops/sales/hourly-trend', {
        params: {
          date: formattedDate,
          shopId: shopId
        },
      });

      const newData = response.data.data || [];
      setHourlyData(newData);
    } catch (error) {
      message.error('Failed to load hourly sales data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle date change for hourly sales
  const handleDateChange = (selectedDate) => {
    setDate(selectedDate);
    fetchHourlySales(selectedDate);
  };

  // Main data fetch effect
  useEffect(() => {
    const fetchData = async () => {
      if (!shopId) {
        console.error("Shop ID not found in local storage");
        return;
      }

      setLoading(true);
      try {
        await fetchTodaysInvoices();
        await fetchHourlySales(date);
        await fetchComparisonData();

        // Fetch product quantities
        const productQuantitiesResponse = await axiosInstance.get(`/products/quantities/${shopId}`);
        setProductCount(productQuantitiesResponse.data.total_product_count || 0);

        // Fetch low stock products
        const lowStockResponse = await axiosInstance.get(`/products/low-stock/${shopId}`);
        const lowStockData = lowStockResponse.data.low_stock_products || [];
        setLowStockCount(lowStockData.length);
        setLowStockProducts(lowStockData);

        // Fetch period sales
        const periodSalesResponse = await axiosInstance.get(`/sales/summary/${shopId}/${salesPeriod}`);
        const periodSalesData = periodSalesResponse.data.sales || [];
        setPeriodSalesData(periodSalesData);

        // Calculate total sales amount
        const totalSales = periodSalesData.reduce((sum, sale) => sum + parseFloat(sale.total), 0);
        setSalesAmount(totalSales);

        // Fetch sales performance
        const salesResponse = await axiosInstance.get(`/sales/performance/${shopId}`);
        const salesData = salesResponse.data.monthly_sales;
        const formattedData = allMonths.map((month, index) => {
          const monthData = salesData.find((item) => item.month === month);
          return { month, sales: monthData ? parseFloat(monthData.total_sales) : 0 };
        });
        setSalesPerformance(formattedData);

        // Calculate progress data
        const progressData = formattedData.map((data, index) => {
          if (index === 0) return { ...data, progress: 0 };
          const previousMonthSales = formattedData[index - 1].sales;
          const progress = previousMonthSales > 0
            ? ((data.sales - previousMonthSales) / previousMonthSales) * 100
            : 0;
          return { ...data, progress: parseFloat(progress.toFixed(2)) };
        });

        setMonthlyProgress(progressData);

        // Calculate overall progress
        const positiveSales = progressData.filter((data) => data.progress > 0);
        const overallProgress = Math.min(
          positiveSales.reduce((sum, data) => sum + data.progress, 0) / (positiveSales.length || 1),
          100
        );
        setSalesProgress(overallProgress);

        // Fetch top selling products
        const productsResponse = await axiosInstance.get(`/sales/top-product/${shopId}`);
        const productsData = productsResponse.data.products || [];
        setTopSellingProducts(productsData);

        // Prepare pie chart data
        const pieData = productsData.map((product) => ({
          type: product.name,
          value: parseFloat(product.quantity_sold),
        }));
        setProductSalesData(pieData);

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [shopId, salesPeriod, comparisonType, comparisonMetric]);

  // Modal handlers
  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  // Generate colors for charts
  const generateColors = (num) => {
    const colors = [
      '#422AFB', '#715FFE', '#A192FD', '#D1C9FF',
      '#4FD1C5', '#38B2AC', '#319795', '#2C7A7B',
      '#F6AD55', '#ED8936', '#DD6B20', '#C05621'
    ];
    return colors.slice(0, num);
  };

  const generateRandomColors = (count) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const hue = Math.floor(Math.random() * 360);
      colors.push(`hsl(${hue}, 70%, 60%)`);
    }
    return colors;
  };

  const hourlyChartConfig = useMemo(() => {
    const sortedData = Array.isArray(hourlyData)
      ? [...hourlyData].sort((a, b) => a.label.localeCompare(b.label))
      : [];

    const coloredData = sortedData.map((item, index) => ({
      ...item,
      color: `hsl(${(index * 360 / sortedData.length) % 360}, 70%, 60%)`
    }));

    return {
      data: coloredData,
      xField: 'label',
      yField: 'total_sales',
      colorField: 'color',
      columnWidthRatio: 0.5,

      legend: false, // 🚫 Hide the list of colors

      animation: {
        appear: {
          animation: 'scale-in-y',
          duration: 300,
        },
        update: {
          animation: 'scale-in-y',
          duration: 300,
        },
      },

      xAxis: {
        title: {
          text: 'Hour',
          style: {
            fontWeight: 600,
            fontSize: 14,
          },
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Total Sales (TZS)',
          style: {
            fontWeight: 600,
            fontSize: 14,
          },
        },
        label: {
          formatter: (val) => `Tsh ${Number(val || 0).toLocaleString()}`,
        },
      },

      tooltip: {
        showTitle: true,
        title: 'label',
        showMarkers: false,
        fields: ['total_sales', 'total_transactions'],
        formatter: (datum) => {
          return {
            name: 'Total Sales',
            value: `${Number(datum.total_sales).toLocaleString()} TZS`,
          };
        },
        customContent: (title, items) => {
          if (!items || items.length === 0) return null;
          const data = items[0].data;
          return `
          <div style="padding: 8px;">
            <div style="margin-bottom: 8px; font-weight: 600;">${title}</div>
            <div>Total Sales: ${Number(data.total_sales).toLocaleString()} TZS</div>
            <div>Transactions: ${data.total_transactions || 0}</div>
          </div>`;
        }
      },

      label: {
        position: 'top',
        layout: [
          { type: 'interval-adjust-position' },
          { type: 'interval-hide-overlap' },
          { type: 'adjust-color' },
        ],
      },

      columnStyle: {
        fillOpacity: 1,
      },

      interactions: [{ type: 'active-region' }],
      padding: [20, 30, 40, 50],
      responsive: true,
      autoFit: true,
    };
  }, [hourlyData]);


  // Sales performance line chart config
  const salesPerformanceConfig = {
    data: salesPerformance,
    xField: 'month',
    yField: 'sales',
    seriesField: 'type',
    lineStyle: {
      stroke: '#422AFB',
      lineWidth: 3,
      shadowColor: '#422AFB',
      shadowBlur: 10,
      shadowOffsetX: 0,
      shadowOffsetY: 4
    },
    color: '#422AFB',
    point: {
      size: 6,
      shape: 'circle',
      style: {
        fill: '#422AFB',
        stroke: '#fff',
        lineWidth: 2
      }
    },
    smooth: true,
    meta: {
      month: { alias: 'Month' },
      sales: { alias: 'Sales (TZS)' }
    },
    yAxis: {
      min: 0,
      grid: {
        line: {
          style: {
            stroke: '#E1E9F8',
            lineDash: [4, 5],
          },
        },
      },
    },
    xAxis: {
      grid: {
        line: {
          style: {
            stroke: '#E1E9F8',
            lineDash: [4, 5],
          },
        },
      },
    },
    interactions: [{ type: 'element-active' }],
    animation: {
      appear: {
        animation: 'path-in',
        duration: 2000,
      },
    },
    areaStyle: {
      fill: 'l(270) 0:#422AFB 1:#ffffff',
      fillOpacity: 0.3,
    },
  };

  // Product sales pie chart data
  const pieChartData = {
    labels: productSalesData.map((data) => data.type),
    datasets: [{
      data: productSalesData.map((data) => data.value),
      backgroundColor: generateColors(productSalesData.length),
      hoverBackgroundColor: generateColors(productSalesData.length).map(color =>
        color.replace(')', ', 0.8)')
      ),
      borderWidth: 0,
    }],
  };

  // Updated comparison chart config
  const comparisonChartConfig = useMemo(() => {
    if (!comparisonData || comparisonData.length === 0) return null;

    // Generate colors based on comparison type
    const colors = generateComparisonColors(comparisonData, comparisonType);

    const commonConfig = {
      color: colors,
      meta: {
        ['total_' + comparisonMetric]: {
          alias: comparisonMetric === 'revenue' ? 'Revenue (TZS)' :
            comparisonMetric === 'quantity' ? 'Quantity Sold' : 'Profit (TZS)'
        }
      },
      yAxis: {
        label: {
          formatter: (val) => comparisonMetric === 'quantity' ? val : `Tsh ${Number(val || 0).toLocaleString()}`,
        },
      },
      label: {
        position: 'top',
        style: {
          fill: '#1B2559',
          fontWeight: 600,
          fontSize: 12,
          shadowBlur: 2,
          shadowColor: 'rgba(0, 0, 0, 0.45)',
        },
        formatter: (datum) => {
          return comparisonMetric === 'quantity' ? datum['total_' + comparisonMetric] :
            `Tsh ${Number(datum['total_' + comparisonMetric]).toLocaleString()}`;
        },
      },
      interactions: [{ type: 'element-active' }],
      columnStyle: {
        radius: [4, 4, 0, 0],
      },
      legend: {
        position: 'bottom',
        itemName: {
          style: {
            fill: '#8F9BBA',
            fontSize: 12,
          },
          formatter: (name) => name.length > 15 ? `${name.substring(0, 12)}...` : name,
        },
      },
      animation: {
        appear: {
          animation: 'wave-in',
          duration: 1000,
        },
        enter: {
          animation: 'wave-in',
          duration: 1000,
        },
      },
    };

    if (comparisonType === 'time_period') {
      return {
        ...commonConfig,
        data: comparisonData,
        xField: 'period',
        yField: 'total_' + comparisonMetric,
        seriesField: 'period',
        meta: {
          ...commonConfig.meta,
          period: { alias: 'Time Period' },
        },
      };
    } else if (comparisonType === 'products') {
      return {
        ...commonConfig,
        data: comparisonData,
        xField: 'product_name',
        yField: 'total_' + comparisonMetric,
        seriesField: 'product_name',
        meta: {
          ...commonConfig.meta,
          product_name: { alias: 'Product' },
        },
      };
    }

    return null;
  }, [comparisonData, comparisonType, comparisonMetric]);

  // Table columns
  const salesTableColumns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      render: (text) => <Tag color="#4FD1C5">{text}</Tag>
    },
    {
      title: 'Unit Price',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (text) => <Text strong>{`Tsh ${(Number(text) || 0).toLocaleString()}`}</Text>
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (text) => (
        <Text strong style={{ color: '#422AFB' }}>
          {`Tsh ${(Number(text) || 0).toLocaleString()}`}
        </Text>
      )
    },
  ];

  const topProductsColumns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Sold',
      dataIndex: 'quantity_sold',
      key: 'quantity_sold',
      align: 'center',
      render: (text) => <Tag color="#4FD1C5">{text}</Tag>
    },
    {
      title: 'Price',
      dataIndex: 'selling_price',
      key: 'selling_price',
      align: 'right',
      render: (text) => <Text strong>{`Tsh ${(Number(text) || 0).toLocaleString()}`}</Text>
    },
  ];

  // Prepare table data
  const salesTableData = periodSalesData.flatMap(sale =>
    sale.sale_items.map(item => ({
      key: item.id,
      product_name: item.product_name,
      quantity: item.quantity,
      price: item.price,
      total: item.total
    }))
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Header Section */}
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Title level={3} style={{ margin: 0, color: '#1B2559', fontWeight: 'bold' }}>
                <motion.span
                  animate={{
                    textShadow: "0 0 8px rgba(66, 42, 251, 0.3)",
                  }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "reverse",
                    duration: 2
                  }}
                >
                  {typingText}
                </motion.span>
              </Title>
            </motion.div>
            <Text type="secondary" style={{ color: '#8F9BBA' }}>
              {currentDateTime}
            </Text>
          </Col>
        </Row>
      </div>

      {/* Stats Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        {/* Total Sales Card */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <motion.div whileHover={{ y: -5 }}>
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                background: 'linear-gradient(81.62deg, #422AFB 2.25%, #715FFE 79.87%)',
                color: 'white'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Total Sales</Text>
                  <Title level={3} style={{ color: 'white', margin: '8px 0', fontWeight: 'bold' }}>
                    Tsh {salesAmount.toLocaleString()}
                  </Title>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {salesProgress >= 0 ? (
                      <ArrowUpOutlined style={{ color: '#01B574', marginRight: '4px' }} />
                    ) : (
                      <ArrowDownOutlined style={{ color: '#E31A1A', marginRight: '4px' }} />
                    )}
                    <Text style={{ color: 'white', fontSize: '12px' }}>

                    </Text>
                  </div>
                </div>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <DollarOutlined style={{ fontSize: '24px', color: 'white' }} />
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>

        {/* Products Card */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <motion.div whileHover={{ y: -5 }}>
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text style={{ color: '#8F9BBA', fontSize: '14px' }}>Total Products</Text>
                  <Title level={3} style={{ color: '#1B2559', margin: '8px 0', fontWeight: 'bold' }}>
                    {productCount}
                  </Title>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowUpOutlined style={{ color: '#01B574', marginRight: '4px' }} />
                    <Text style={{ color: '#8F9BBA', fontSize: '12px' }}>

                    </Text>
                  </div>
                </div>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  background: '#F4F7FE',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <ShoppingCartOutlined style={{ fontSize: '24px', color: '#422AFB' }} />
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>

        {/* Today's Invoices Card */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <motion.div whileHover={{ y: -5 }}>
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text style={{ color: '#8F9BBA', fontSize: '14px' }}>Today's Invoices</Text>
                  <Title level={3} style={{ color: '#1B2559', margin: '8px 0', fontWeight: 'bold' }}>
                    {todaysInvoicesCount}
                  </Title>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowUpOutlined style={{ color: '#01B574', marginRight: '4px' }} />
                    <Text style={{ color: '#8F9BBA', fontSize: '12px' }}>

                    </Text>
                  </div>
                </div>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  background: '#F4F7FE',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <FileTextOutlined style={{ fontSize: '24px', color: '#422AFB' }} />
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>

        {/* Low Stock Card */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <motion.div whileHover={{ y: -5 }}>
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text style={{ color: '#8F9BBA', fontSize: '14px' }}>Low Stock Items</Text>
                  <Title level={3} style={{
                    color: lowStockCount > 0 ? '#E31A1A' : '#1B2559',
                    margin: '8px 0',
                    fontWeight: 'bold'
                  }}>
                    {lowStockCount}
                  </Title>
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={showModal}
                    style={{
                      paddingLeft: 0,
                      color: '#422AFB',
                      fontWeight: 500,
                      fontSize: '12px'
                    }}
                  >
                    View Details
                  </Button>
                </div>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  background: '#F4F7FE',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <WarningOutlined style={{ fontSize: '24px', color: '#E31A1A' }} />
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        {/* Hourly Sales Trend */}
        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ color: '#1B2559', fontSize: '16px' }}>
                    <CalendarOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                    Hourly Sales Trend
                  </Text>
                  <DatePicker
                    value={date}
                    onChange={handleDateChange}
                    disabledDate={(current) => current && current > moment().endOf('day')}
                    allowClear={false}
                    style={{ width: '150px' }}
                    suffixIcon={<CalendarOutlined />}
                  />
                </div>
              }
            >
              <div style={{ minHeight: '300px' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', paddingTop: 100 }}>
                    <Spin size="large" />
                  </div>
                ) : hourlyData && hourlyData.length > 0 ? (
                  <Column {...hourlyChartConfig} height={300} />
                ) : (
                  <Empty description="No sales data available for this date" />
                )}
              </div>
            </Card>
          </motion.div>
        </Col>

        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              title={
                <Text strong style={{ color: '#1B2559', fontSize: '16px' }}>
                  <FileTextOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                  Recent Sales
                </Text>
              }
              bodyStyle={{ padding: 0 }}
              extra={
                <Select
                  value={salesPeriod}
                  onChange={setSalesPeriod}
                  style={{ width: 120 }}
                  bordered={false}
                  suffixIcon={<CalendarOutlined />}
                >
                  <Option value="today">Today</Option>
                  <Option value="week">Week</Option>
                  <Option value="month">Month</Option>
                  <Option value="year">Year</Option>
                </Select>
              }
            >
              <Table
                columns={salesTableColumns}
                dataSource={salesTableData}
                rowKey="key"
                size="middle"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  style: { padding: '0 16px 16px' }
                }}
                scroll={{ x: true }}
                style={{
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  borderTop: 'none'
                }}
                rowClassName={() => 'hover-row'}
              />
            </Card>
          </motion.div>
        </Col>
      </Row>



      {/* Sales Comparison Section */}
      {/* <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card
              bordered={false}
              loading={comparisonLoading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ color: '#1B2559', fontSize: '16px' }}>
                    <BarChartOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                    Sales Comparison
                  </Text>
                  <Space>
                    <Select
                      value={comparisonType}
                      onChange={setComparisonType}
                      style={{ width: 180 }}
                      bordered={false}
                    >
                      <Option value="time_period">Time Periods</Option>
                      <Option value="products">Products</Option>
                      <Option value="customers">Customers</Option>
                      <Option value="employees">Employees</Option>
                      <Option value="payment_methods">Payment Methods</Option>
                    </Select>
                    <Select
                      value={comparisonMetric}
                      onChange={setComparisonMetric}
                      style={{ width: 120 }}
                      bordered={false}
                    >
                      <Option value="revenue">Revenue</Option>
                      <Option value="quantity">Quantity</Option>
                      <Option value="profit">Profit</Option>
                    </Select>
                  </Space>
                </div>
              }
            >
              <div style={{ height: Math.max(400, comparisonData.length * 40) }}>
                {comparisonData.length > 0 && comparisonChartConfig ? (
                  <Column {...comparisonChartConfig} />
                ) : (
                  <Empty description="No comparison data available" style={{ marginTop: 80 }} />
                )}
              </div>
            </Card>
          </motion.div>
        </Col>
      </Row> */}


      {/* Charts Row */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        {/* Sales Performance Chart */}
        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              title={
                <Text strong style={{ color: '#1B2559', fontSize: '16px' }}>
                  <BarChartOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                  Monthly Sales Performance
                </Text>
              }
            >
              <Line {...salesPerformanceConfig} height={300} />
            </Card>
          </motion.div>
        </Col>

        {/* Top Products Pie Chart */}
        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              title={
                <Text strong style={{ color: '#1B2559', fontSize: '16px' }}>
                  <PieChartOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                  Product Sales Distribution
                </Text>
              }
            >
              {productSalesData.length > 0 ? (
                <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
                  <Doughnut
                    data={pieChartData}
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'right',
                          labels: {
                            font: {
                              size: 12
                            },
                            padding: 20,
                            usePointStyle: true,
                            pointStyle: 'circle'
                          }
                        },
                        tooltip: {
                          bodyFont: {
                            size: 14
                          },
                          displayColors: true,
                          usePointStyle: true,
                          callbacks: {
                            label: function (context) {
                              const label = context.label || '';
                              const value = context.raw || 0;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = Math.round((value / total) * 100);
                              return `${label}: ${value} (${percentage}%)`;
                            }
                          }
                        }
                      },
                      cutout: '65%',
                      borderRadius: 4,
                    }}
                  />
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ margin: '40px 0' }}
                />
              )}
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Recent Sales Table */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>

        <Col xs={24} lg={24}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card
              bordered={false}
              loading={loading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              title={
                <Text strong style={{ color: '#1B2559', fontSize: '16px' }}>
                  <StarOutlined style={{ marginRight: 8, color: '#F6AD55' }} />
                  Top Selling Products
                </Text>
              }
              bodyStyle={{ padding: 0 }}
            >
              <Table
                columns={topProductsColumns}
                dataSource={topSellingProducts}
                rowKey="id"
                size="middle"
                pagination={{
                  pageSize: 25,
                  showSizeChanger: false,
                  style: { padding: '0 16px 16px' }
                }}
                scroll={{ x: true }}
                style={{
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  borderTop: 'none'
                }}
              />
            </Card>
          </motion.div>
        </Col>
      </Row>


      {/* Low Stock Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#E31A1A' }} />
            <Text strong>Low Stock Products</Text>
          </Space>
        }
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        style={{ borderRadius: '16px' }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={[
            {
              title: 'Product',
              dataIndex: 'name',
              key: 'name',
              render: (text) => <Text strong>{text}</Text>
            },
            {
              title: 'Quantity',
              dataIndex: 'quantity',
              key: 'quantity',
              render: (text) => <Tag color={text < 5 ? '#E31A1A' : '#F6AD55'}>{text}</Tag>
            },
            {
              title: 'Price',
              dataIndex: 'selling_price',
              key: 'selling_price',
              align: 'right',
              render: (text) => <Text strong>{`Tsh ${(Number(text) || 0).toLocaleString()}`}</Text>
            },
          ]}
          dataSource={lowStockProducts}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          scroll={{ x: true }}
        />
      </Modal>

      {/* Custom CSS for table hover effect */}
      <style jsx global>{`
        .hover-row:hover {
          background-color: #F4F7FE !important;
          transition: background-color 0.3s ease;
        }
        .ant-table-thead > tr > th {
          background-color: #F4F7FE !important;
          color: #1B2559 !important;
          font-weight: 600 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #E1E9F8 !important;
        }
        .ant-table-container {
          border-radius: 12px !important;
          overflow: hidden !important;
        }
      `}</style>
    </div>
  );
};

export default HomePage;